import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";
import { LTTB } from "downsample";

const BalanceChart = ({
  balance_chart,
  maxLoss,
  accountType,
  profit,
  maxDailyLoss,
}) => {

  const balanceData = balance_chart.map((entry) => entry.balance);
  const equityData = balance_chart.map((entry) => entry.equity);
  const formattedDates = balance_chart.map((entry) =>
    new Date(entry.time).getTime()
  );

  let smooth = [];
  let separatedData = [];


  if (balanceData.length && equityData.length && formattedDates.length) {
    const combinedData = formattedDates.map((date, idx) => [
      date,
      balanceData[idx],
      equityData[idx],
    ]);

    try {
   
      smooth = LTTB(combinedData, 150);

   
      separatedData = [
        smooth.map((item) => item[0]), 
        smooth.map((item) => item[1]), 
        smooth.map((item) => item[2]), 
      ];
    } catch (error) {
      console.error("Error smoothing data:", error);
    }
  }

 
  const series = [
    {
      name: "Balance",
      data:
        separatedData[1]?.map((balance, idx) => [
          separatedData[0][idx], 
          balance, 
        ]) || [],
    },
    {
      name: "Equity",
      data:
        separatedData[2]?.map((equity, idx) => [
          separatedData[0][idx], 
          equity,
        ]) || [],
    },
  ];


  const options = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 350,
        toolbar: {
          show: false,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
        },
      },
      colors: ["#c7f464", "#4ecdc4", "#64C144", "#FD504F", "#ff154498"],
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },

      xaxis: {
        type: "datetime",
        title: {
          text: "Date",
          style: {
            color: "#FFF",
            fontFamily: "Outfit",
            fontSize: "10px",
          },
        },
        labels: {
          style: {
            colors: "#FFF",
            fontSize: "10px",
          },
          formatter: (value) => dayjs(value).format("MMM D"),
        },
      },
      yaxis: {
        min: maxLoss - 500,
        max: Math.max(
          profit,
          separatedData[1] ? Math.max(...separatedData[1]) : 0
        ),
        title: {
          text: "Amount (USD)",
          style: {
            color: "#FFF",
            fontFamily: "Outfit",
            fontSize: "10px",
          },
        },
        labels: {
          formatter: (value) => {
            if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
            if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
            return `$${value.toFixed(0)}`;
          },
        },
      },
      tooltip: {
        theme: "dark",
        x: {
          formatter: (value) =>
            new Date(value).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            }),
        },
        y: {
          formatter: (value) => `$${value.toFixed(2)}`,
        },
      },
      annotations: {
        yaxis: [
          {
            y: maxLoss,
            borderColor: "red",
            borderWidth: 3,
            strokeDashArray: 0,
            label: {
              borderColor: "red",
              style: {
                color: "#fff",
                background: "red",
              },
              text: "Max Loss",
            },
          },
          ...(accountType?.toLowerCase() !== "funded"
            ? [
                {
                  y: profit,
                  borderColor: "green",
                  borderWidth: 3,
                  strokeDashArray: 0,
                  label: {
                    borderColor: "green",
                    style: {
                      color: "#fff",
                      background: "green",
                    },
                    text: "Profit Target",
                  },
                },
              ]
            : []),
          {
            y: maxDailyLoss,
            borderColor: "#ff154498",
            borderWidth: 3,
            strokeDashArray: 0,
            label: {
              borderColor: "#ff154498",
              style: {
                color: "#fff",
                background: "#ff154498",
              },
              text: "Max Daily Loss",
            },
          },
        ],
      },
    };
  }, [maxLoss, profit, maxDailyLoss, accountType, separatedData]);

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="line"
      height={380}
      style={{ margin: "0 !important" }}
    />
  );
};

export default BalanceChart;
